import { createAsyncThunk, createSlice, } from "@reduxjs/toolkit";
import * as api from "@repo/shared/api";
import { analytics } from "../../analytics/port";
import { patchUserSettings, } from "../../api/users/patchUserSettings";
import { postUsedFeature } from "../../api/users/postUsedFeature";
import { fetchClubStats } from "../myClub/myClubSlice";
import { setCachedImportedClub } from "../permissions/coldStart";
import { fetchCredits } from "../permissions/permissionsSlice";
// Initial state
const initialState = {
    email: null,
    name: null,
    uuid: null,
    subscription: "free",
    billingProvider: null,
    jwt: null,
    platform: "Playstation",
    paid: false,
    clubLinked: false,
    status: "idle",
    usedFeatures: [],
    allowTracking: false,
    emailNotification: false,
};
export const fetchProfile = createAsyncThunk("user/fetchProfile", async () => {
    const response = await api.getProfile();
    // Identify before any subsequent track call so events attach to the backend
    // User uuid (the same id RevenueCat uses as app_user_id, ADR 0003) instead
    // of the anonymous device id — mirrors the web userSlice's fetchUser.
    const uuid = response?.user?.uuid;
    if (typeof uuid === "string" && uuid.length > 0) {
        analytics.identify(uuid);
    }
    return response;
});
/**
 * Records that the user has seen or used a feature. Signed-in only, and only
 * the first time; the account follows once the post lands.
 */
export const usedFeature = createAsyncThunk("user/usedFeature", async ({ feature }, { getState }) => {
    const { userReducer } = getState();
    if (!userReducer.uuid || userReducer.usedFeatures?.includes(feature))
        return null;
    await postUsedFeature(feature);
    return feature;
});
/**
 * Saves account settings. Shown at once; put back if the save fails, so the
 * screen never claims a setting the account does not have.
 */
export const updateUserSettings = createAsyncThunk("user/updateUserSettings", async (settings) => {
    await patchUserSettings(settings);
    return settings;
});
// Settle a just-completed purchase. Verify first: the profile only carries the
// new tier once it returns. Credits too — tier and balance are separate.
export const settleEntitlement = createAsyncThunk("user/settleEntitlement", async (_, { dispatch }) => {
    await api.verifyRevenueCatEntitlements();
    await Promise.all([dispatch(fetchProfile()), dispatch(fetchCredits())]);
});
// Unlink clears lastImportedAt, which the club-import gate reads off the stats.
export const unlinkClub = createAsyncThunk("user/unlinkClub", async (_, { dispatch }) => {
    await api.unlinkClub();
    setCachedImportedClub(false);
    await dispatch(fetchClubStats());
});
export const loginWithGoogle = createAsyncThunk("user/loginWithGoogle", async (idToken) => {
    const response = await api.googleLogin(idToken);
    return response;
});
export const loginWithApple = createAsyncThunk("user/loginWithApple", async (params) => {
    const response = await api.appleLogin(params);
    return response;
});
export const loginWithEmail = createAsyncThunk("user/loginWithEmail", async (email, { rejectWithValue }) => {
    try {
        const response = await api.loginWithEmail(email);
        return response;
    }
    catch (error) {
        const status = error?.response?.status;
        const message = error?.response?.data?.message || error?.message || "Unknown error";
        return rejectWithValue({ status, message });
    }
});
export const confirmLoginWithCode = createAsyncThunk("user/confirmLoginWithCode", async ({ email, code }, { rejectWithValue }) => {
    try {
        const response = await api.confirmLoginWithCode(email, code);
        return response;
    }
    catch (error) {
        const status = error?.response?.status;
        const message = error?.response?.data?.message || error?.message || "Unknown error";
        return rejectWithValue({ status, message });
    }
});
// Create the slice
const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
        // Set user data (e.g., after login)
        setUser: (state, action) => {
            state.email = action.payload.email;
            state.name = action.payload.name;
        },
        // Clear user data (e.g., after logout)
        clearUser: (state) => {
            state.email = null;
            state.name = null;
        },
        // Update user name
        updateUserName: (state, action) => {
            state.name = action.payload;
        },
        logout: (state) => {
            state.email = null;
            state.name = null;
            state.uuid = null;
            state.jwt = null;
            state.subscription = "free";
            state.billingProvider = null;
            state.paid = false;
            state.clubLinked = false;
            state.status = "idle";
            state.usedFeatures = [];
        },
        setJwt: (state, action) => {
            state.jwt = action.payload;
        },
        /** For a feature the backend records itself, e.g. the best squad. */
        addUsedFeature: (state, action) => {
            if (!state.usedFeatures.includes(action.payload))
                state.usedFeatures.push(action.payload);
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchProfile.pending, (state) => {
            state.status = "pending";
        });
        builder.addCase(fetchProfile.fulfilled, (state, action) => {
            state.email = action.payload.user.email;
            state.uuid = action.payload.user.uuid ?? null;
            state.subscription = action.payload.user.subscription;
            state.billingProvider = action.payload.user.billingProvider ?? null;
            state.platform = action.payload.user.platform;
            // See UserState.paid — the payload carries `subscription`, not `paid`.
            state.paid = action.payload.user.subscription !== "free";
            state.usedFeatures = action.payload.user.usedFeatures ?? [];
            state.allowTracking = !!action.payload.user.allowTracking;
            state.emailNotification = !!action.payload.user.emailNotification;
            state.clubLinked = !!action.payload.user.clubLinked;
            state.status = "fulfilled";
        });
        builder.addCase(updateUserSettings.pending, (state, action) => {
            Object.assign(state, action.meta.arg);
        });
        builder.addCase(updateUserSettings.rejected, (state, action) => {
            // Back to what the account still has.
            const { platform, allowTracking, emailNotification } = action.meta.arg;
            if (platform !== undefined)
                state.platform = platform === "PC" ? "Playstation" : "PC";
            if (allowTracking !== undefined)
                state.allowTracking = !allowTracking;
            if (emailNotification !== undefined)
                state.emailNotification = !emailNotification;
        });
        builder.addCase(usedFeature.fulfilled, (state, action) => {
            if (action.payload && !state.usedFeatures.includes(action.payload))
                state.usedFeatures.push(action.payload);
        });
        builder.addCase(fetchProfile.rejected, (state) => {
            state.status = "rejected";
        });
        // Both a 204 and a 404 leave no claim, so no profile refetch.
        builder.addCase(unlinkClub.fulfilled, (state) => {
            state.clubLinked = false;
        });
        builder.addCase(loginWithGoogle.fulfilled, (state, action) => {
            state.email = action.payload.email;
            state.jwt = action.payload.access_token;
        });
        builder.addCase(loginWithApple.fulfilled, (state, action) => {
            state.email = action.payload.email;
            state.jwt = action.payload.access_token;
        });
        builder.addCase(loginWithEmail.fulfilled, (state, action) => {
            state.email = action.payload.email;
            state.jwt = action.payload.access_token;
        });
        builder.addCase(confirmLoginWithCode.fulfilled, (state, action) => {
            state.jwt = action.payload.access_token;
        });
    },
});
// Export actions
export const { setUser, clearUser, updateUserName, logout, setJwt, addUsedFeature, } = userSlice.actions;
// Export reducer
export default userSlice.reducer;
export const getUserSelector = (state) => state.userReducer;
export const usedFeaturesSelector = (state) => state.userReducer.usedFeatures;
/**
 * True as soon as either the token or the identity is known, rather than once
 * users/profile has answered — every permissions check keys off this, and
 * waiting for that request would give a returning user the logged-out
 * treatment for its duration.
 *
 * Both halves matter, and which one arrives first differs by platform: mobile
 * restores `jwt` from SecureStore during bootstrap, while `uuid` only lands
 * with the profile. Mirrors web's `uuid || access_token` check.
 */
export const userIsLoggedInSelector = (state) => !!state.userReducer.uuid || !!state.userReducer.jwt;
//# sourceMappingURL=userSlice.js.map